import { JobStatus } from 'cli/enums';
import { ICliJob } from 'cli/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { PricingSourceType } from 'pricing/enums';
import { ApplySelectedSpaSkipReason, TradeAgreementUnavailableReason } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import {
  IApplySelectedSpaJobProgress,
  IApplySelectedSpaJobResult,
  IApplySelectedSpaOptions,
  IApplySelectedSpaSkippedLine,
  ICustomerQuotePart,
} from 'quotes/types';
import {
  getCostSourceTypeInUse,
  getSelectedSpaPick,
  ISelectedSpaPick,
  isSelectSpaBlockedForQuotePart,
} from 'quotes/utils';

export interface IApplySelectedSpaSkippedGroup {
  labelKey: string;
  lines: IApplySelectedSpaSkippedLine[];
  reason: string;
}

export interface IApplySelectedSpaSummary {
  key: string;
  values: Record<string, unknown>;
}

/**
 * Any new or changed pick (another version included) is worth a prompt; re-saving the same is not.
 */
export const getAppliedSelectedSpaCode = (
  savedQuotePart?: ICustomerQuotePart | null,
  previousSelectedSpa?: ISelectedSpaPick | null
): string | null => {
  const pick = getSelectedSpaPick(savedQuotePart);

  if (!pick) {
    return null;
  }

  const isUnchanged =
    pick.spaCostSource === previousSelectedSpa?.spaCostSource &&
    pick.spaCostSourceId === (previousSelectedSpa?.spaCostSourceId || null);

  return isUnchanged ? null : pick.spaCostSource;
};

// These take the agreement only when their option is ticked. A row has no `isCostingCustom`: its
// manual state is the `custom` type.
const selectedSpaKeptCostSourceTypes: PricingSourceType[] = [
  PricingSourceType.CUSTOM,
  PricingSourceType.SELECTED_SPA,
];

export const isCostOpenToSelectedSpa = (quotePart?: ICustomerQuotePart | null): boolean =>
  !selectedSpaKeptCostSourceTypes.includes(getCostSourceTypeInUse(quotePart));

export const DEFAULT_APPLY_SELECTED_SPA_OPTIONS: IApplySelectedSpaOptions = {
  replaceManualCosts: true,
  replaceSelectedSpas: true,
};

// `open` always takes the agreement, the other two only with their option ticked.
export interface IApplySelectedSpaLineCounts {
  manualCost: number;
  open: number;
  otherSelectedSpa: number;
}

const isSameSelectedSpaPick = (first: ISelectedSpaPick | null, second: ISelectedSpaPick | null) =>
  !!first &&
  !!second &&
  first.spaCostSource === second.spaCostSource &&
  first.spaCostSourceId === second.spaCostSourceId;

const getApplySelectedSpaLineKind = (
  row: ICustomerQuotePart,
  savedQuotePartId: number | undefined,
  savedPick: ISelectedSpaPick | null
): keyof IApplySelectedSpaLineCounts | null => {
  if (
    !row?.id ||
    row.id === savedQuotePartId ||
    row.deleted ||
    !row.alternateVersionEnabled ||
    isSelectSpaBlockedForQuotePart(row)
  ) {
    return null;
  }

  if (isCostOpenToSelectedSpa(row)) {
    return 'open';
  }

  if (PricingSourceType.CUSTOM === getCostSourceTypeInUse(row)) {
    return 'manualCost';
  }

  // The same code and version is nothing to apply, whatever the options say.
  return isSameSelectedSpaPick(getSelectedSpaPick(row), savedPick) ? null : 'otherSelectedSpa';
};

/**
 * Judged from the grid rows alone; the job derives the real candidates. The engine flag is not
 * read: rows priced before it existed carry none.
 */
export const countApplySelectedSpaLines = (
  rows: ICustomerQuotePart[] | null | undefined,
  savedQuotePart?: ICustomerQuotePart | null
): IApplySelectedSpaLineCounts => {
  const savedPick = getSelectedSpaPick(savedQuotePart);

  return (rows ?? []).reduce<IApplySelectedSpaLineCounts>(
    (counts, row) => {
      const kind = getApplySelectedSpaLineKind(row, savedQuotePart?.id, savedPick);

      if (kind) {
        counts[kind] += 1;
      }

      return counts;
    },
    { manualCost: 0, open: 0, otherSelectedSpa: 0 }
  );
};

export const hasApplySelectedSpaCandidates = (
  { manualCost, open, otherSelectedSpa }: IApplySelectedSpaLineCounts,
  { replaceManualCosts, replaceSelectedSpas }: IApplySelectedSpaOptions
): boolean =>
  open > 0 ||
  (replaceManualCosts && manualCost > 0) ||
  (replaceSelectedSpas && otherSelectedSpa > 0);

export const getApplySelectedSpaLineLabel = ({
  lineNumber,
  quotePartId,
}: Pick<IApplySelectedSpaSkippedLine, 'lineNumber' | 'quotePartId'>): string =>
  lineNumber ?? String(quotePartId);

// Worded for a list of lines, unlike the single-line picker's `messageTradeAgreementsReason*`.
const notEligibleReasonKeys: Record<string, string> = {
  [TradeAgreementUnavailableReason.COST_LOCKED]: 'messageSelectedSpaSkippedCostLocked',
  [TradeAgreementUnavailableReason.DISCOUNT_LINE]: 'messageSelectedSpaSkippedDiscountLine',
  [TradeAgreementUnavailableReason.LOCKED]: 'messageSelectedSpaSkippedLocked',
  [TradeAgreementUnavailableReason.NO_ENTITY_CODE]: 'messageSelectedSpaSkippedNoEntityCode',
  [TradeAgreementUnavailableReason.NO_SELECTION_AVAILABLE]:
    'messageSelectedSpaSkippedNoSelectionAvailable',
  [TradeAgreementUnavailableReason.NO_SKU_UOM]: 'messageSelectedSpaSkippedNoSkuUom',
  [TradeAgreementUnavailableReason.NOT_ALTERNATE]: 'messageSelectedSpaSkippedNotAlternate',
  [TradeAgreementUnavailableReason.OMITTED]: 'messageSelectedSpaSkippedOmitted',
  [TradeAgreementUnavailableReason.SELECTION_UNKNOWN]: 'messageSelectedSpaSkippedSelectionUnknown',
  [TradeAgreementUnavailableReason.SUBSTITUTED]: 'messageSelectedSpaSkippedSubstituted',
};

const skipReasonKeys: Record<string, string> = {
  [ApplySelectedSpaSkipReason.ALREADY_SELECTED]: 'messageSelectedSpaSkippedAlreadySelected',
  [ApplySelectedSpaSkipReason.ALREADY_THIS_SPA]: 'messageSelectedSpaSkippedAlreadyThisSpa',
  [ApplySelectedSpaSkipReason.LINE_ERROR]: 'messageSelectedSpaSkippedLineError',
  [ApplySelectedSpaSkipReason.MANUAL_COST]: 'messageSelectedSpaSkippedManualCost',
  [ApplySelectedSpaSkipReason.NOT_IN_LIST]: 'messageSelectedSpaSkippedNotInList',
  [ApplySelectedSpaSkipReason.SAVE_FAILED]: 'messageSelectedSpaSkippedSaveFailed',
};

// The first rule that holds is the reason a line was skipped.
const skipReasonOrder: string[] = [
  ApplySelectedSpaSkipReason.ALREADY_THIS_SPA,
  ApplySelectedSpaSkipReason.ALREADY_SELECTED,
  ApplySelectedSpaSkipReason.MANUAL_COST,
  ApplySelectedSpaSkipReason.NOT_ELIGIBLE,
  ApplySelectedSpaSkipReason.NOT_IN_LIST,
  ApplySelectedSpaSkipReason.LINE_ERROR,
  ApplySelectedSpaSkipReason.SAVE_FAILED,
];

// `not_eligible:<reason>` carries the /quote/part/trade-agreements reason after the colon.
const splitSkipReason = (reason?: string | null): { kind: string; detail: string } => {
  const [kind, ...detail] = (reason ?? '').split(':');

  return { kind, detail: detail.join(':') };
};

export const getApplySelectedSpaSkipReasonKey = (reason?: string | null): string => {
  const { kind, detail } = splitSkipReason(reason);

  if (ApplySelectedSpaSkipReason.NOT_ELIGIBLE === kind) {
    return notEligibleReasonKeys[detail] ?? 'messageSelectedSpaSkippedNotEligible';
  }

  return skipReasonKeys[kind] ?? 'messageSelectedSpaSkippedOther';
};

const getSkipReasonRank = (reason: string): number => {
  const rank = skipReasonOrder.indexOf(splitSkipReason(reason).kind);

  return -1 === rank ? skipReasonOrder.length : rank;
};

/**
 * Grouped by wording, not by the raw reason, so two unworded reasons do not repeat one label.
 */
export const groupSkippedLinesByReason = (
  skipped?: IApplySelectedSpaSkippedLine[] | null
): IApplySelectedSpaSkippedGroup[] => {
  const groups = (skipped ?? []).reduce((acc, line) => {
    const labelKey = getApplySelectedSpaSkipReasonKey(line.reason);
    const group = acc.get(labelKey) ?? { labelKey, lines: [], reason: line.reason };

    group.lines.push(line);
    acc.set(labelKey, group);

    return acc;
  }, new Map<string, IApplySelectedSpaSkippedGroup>());

  return Array.from(groups.values()).sort(
    (first, second) => getSkipReasonRank(first.reason) - getSkipReasonRank(second.reason)
  );
};

// Shown under the line unless it only repeats the reason it is listed under.
export const getApplySelectedSpaSkippedLineMessage = (
  { message }: Pick<IApplySelectedSpaSkippedLine, 'message'>,
  groupLabel: string
): string | null => {
  const text = message?.trim();

  return text && text !== groupLabel.trim() ? text : null;
};

// Each line commits on its own, so a stopped job may still report progress.
export const hasApplySelectedSpaJobProgress = (result?: IApplySelectedSpaJobResult | null) =>
  !!result?.applied?.length || !!result?.skipped?.length;

// The counts lag the saves by up to 4 lines: only the last report (`processed === total`) with
// nothing applied proves that nothing was saved.
export const mayApplySelectedSpaJobHaveSavedLines = (
  lastProgress?: IApplySelectedSpaJobProgress | null
): boolean =>
  !lastProgress || lastProgress.applied > 0 || lastProgress.processed < lastProgress.total;

// `quote_parts` may miss applied lines, and a stopped job may carry no report at all.
export const shouldReloadAfterApplySelectedSpaJob = (
  result: IApplySelectedSpaJobResult,
  {
    isStopped = false,
    lastProgress = null,
  }: { isStopped?: boolean; lastProgress?: IApplySelectedSpaJobProgress | null } = {}
): boolean => {
  if (!isStopped || hasApplySelectedSpaJobProgress(result)) {
    return result.applied.length > result.quoteParts.length;
  }

  return mayApplySelectedSpaJobHaveSavedLines(lastProgress);
};

export const getApplySelectedSpaSummary = (
  result: IApplySelectedSpaJobResult | null,
  { agreementCode, isStopped = false }: { agreementCode?: string | null; isStopped?: boolean }
): IApplySelectedSpaSummary => {
  const applied = result?.applied ?? [];

  if (isStopped) {
    return applied.length
      ? {
          key: 'messageSelectedSpaApplyStoppedSummary',
          values: { agreementCode, count: applied.length },
        }
      : { key: 'messageSelectedSpaApplyStoppedNothingApplied', values: { agreementCode } };
  }

  if (!applied.length) {
    const hasSkip = (kind: ApplySelectedSpaSkipReason) =>
      (result?.skipped ?? []).some(({ reason }) => kind === splitSkipReason(reason).kind);

    // A failed lookup or save is not "no other line could take it": trying again may apply it.
    if (hasSkip(ApplySelectedSpaSkipReason.LINE_ERROR)) {
      return { key: 'messageSelectedSpaApplyNothingAppliedLineError', values: { agreementCode } };
    }

    return {
      key: hasSkip(ApplySelectedSpaSkipReason.SAVE_FAILED)
        ? 'messageSelectedSpaApplyNothingAppliedSaveFailed'
        : 'messageSelectedSpaApplyNothingApplied',
      values: { agreementCode },
    };
  }

  return {
    key: 'messageSelectedSpaApplySummary',
    values: {
      agreementCode,
      applied: result?.totals?.applied,
      count: result?.totals?.lines,
      skipped: result?.totals?.skipped,
    },
  };
};

/**
 * A full sentence: the toast template appends its own, and BE descriptions often end with a period.
 */
export const getApplySelectedSpaStopDescription = (
  { status, statusDescription }: Pick<ICliJob, 'status' | 'statusDescription'>,
  expiredDescription: string
): string => {
  const description =
    statusDescription?.trim() || (JobStatus.EXPIRED === status ? expiredDescription : status);

  if (!description || /[.!?]$/.test(description)) {
    return description ?? '';
  }

  return `${description}.`;
};

/**
 * The rows the job saved override the grid's. A line without a part number stays "Line N".
 */
export const getApplySelectedSpaPartNumbers = (
  gridRows?: ICustomerQuotePart[] | null,
  jobQuoteParts?: ICustomerQuotePart[] | null
): Record<number, string> =>
  [...(gridRows ?? []), ...(jobQuoteParts ?? [])].reduce<Record<number, string>>((acc, row) => {
    const partNumber = row?.id ? QuotePartHelper.getDisplayedPartNumber(row) : null;

    if (partNumber && DEFAULT_PLACEHOLDER !== partNumber) {
      acc[row.id] = partNumber;
    }

    return acc;
  }, {});
